import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import dayjs from 'dayjs'

import { useAuthStore } from '@/stores/auth'
import { useAppSettings } from '@/stores/useAppSettings'

// Initialize dayjs plugins
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(relativeTime)

type FormatStyle = 'short' | 'medium' | 'long' | 'full' | 'custom' | 'relative' | 'dateTime'
type TimeFormat = '12h' | '24h'
type DateFormat = 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD' | 'DD MMM YYYY' | 'MMMM DD, YYYY' | 'dddd, MMMM DD, YYYY'

// Map backend date format to dayjs format
const DATE_FORMAT_MAP: Record<DateFormat, string> = {
  'DD/MM/YYYY': 'DD/MM/YYYY',
  'MM/DD/YYYY': 'MM/DD/YYYY',
  'YYYY-MM-DD': 'YYYY-MM-DD',
  'DD MMM YYYY': 'DD MMM YYYY',
  'MMMM DD, YYYY': 'MMMM DD, YYYY',
  'dddd, MMMM DD, YYYY': 'dddd, MMMM DD, YYYY',
}

/**
 * Get user's selected timezone with proper fallback chain:
 * 1. User profile timezone (from auth store)
 * 2. App localization setting (system default)
 * 3. UTC (final fallback)
 */
const getUserTimezone = (): string => {
  try {
    // Priority 1: User's personal timezone from profile
    const user = useAuthStore.getState().user
    if (user?.timezone) {
      return user.timezone
    }

    // Priority 2: System-wide timezone setting
    return useAppSettings.getState().getSetting('localization.timezone', 'UTC')
  } catch {
    // Priority 3: Safe fallback
    return 'UTC'
  }
}

/**
 * Get current date/time in user's selected timezone
 * Use this instead of `new Date()` when you need timezone-aware "now"
 * @returns Date object representing current moment in user's selected timezone
 */
export const now = (): Date => {
  const tz = getUserTimezone()
  return dayjs().tz(tz).toDate()
}

// Get date format from settings or use default
const getDateFormatPattern = (): string => {
  try {
    const settingValue: string = useAppSettings.getState().getSetting('localization.date_format', 'DD/MM/YYYY')
    return DATE_FORMAT_MAP[settingValue as DateFormat] || 'DD/MM/YYYY'
  } catch {
    return 'DD/MM/YYYY'
  }
}

const formatPresets: Record<Exclude<FormatStyle, 'custom' | 'relative' | 'dateTime'>, string> = {
  short: 'DD/MM/YYYY', // Will be dynamically replaced
  medium: 'DD MMM YYYY',
  long: 'MMMM DD, YYYY',
  full: 'dddd, MMMM DD, YYYY',
}

// Build datetime presets dynamically based on date format setting
const getDateTimePresets = (timeFormat: TimeFormat): Record<'short' | 'medium' | 'long' | 'full', string> => {
  const datePattern = getDateFormatPattern()
  const timePattern24h = 'HH:mm'
  const timePattern12h = 'h:mm A'
  const timePatternFull24h = 'HH:mm:ss'
  const timePatternFull12h = 'h:mm:ss A'

  const timePattern = timeFormat === '12h' ? timePattern12h : timePattern24h
  const timePatternFull = timeFormat === '12h' ? timePatternFull12h : timePatternFull24h

  return {
    short: `${datePattern} ${timePattern}`,
    medium: `DD MMM YYYY, ${timePattern}`,
    long: `MMMM DD, YYYY ${timePattern}`,
    full: `dddd, MMMM DD, YYYY ${timePatternFull}`,
  }
}

/**
 * Standardized date formatter with timezone awareness
 * Respects app settings for time format (12h/24h), date format, and timezone
 */
export function formatDate(
  date: Date | string | null | undefined,
  options: {
    style?: FormatStyle
    fallback?: string
    formatStr?: string // only used if style === 'custom'
    addSuffix?: boolean // only used for relative
    dateTimeStyle?: 'short' | 'medium' | 'long' | 'full' // control datetime preset
    timeFormat?: TimeFormat // override time format (12h/24h), defaults to app setting
    timezone?: string // optional timezone override, defaults to user's selected timezone
    showTime?: boolean // if true, automatically adds time to the date format
  } = {}
): string {
  const {
    style = 'medium',
    fallback = '—',
    formatStr,
    addSuffix = true,
    dateTimeStyle = 'medium',
    timeFormat,
    timezone: tzOverride,
    showTime = false,
  } = options

  if (!date) return fallback

  // Get timezone: override > user setting > UTC fallback
  const tz = tzOverride || getUserTimezone()

  // Parse as UTC and convert to user's timezone
  const d = dayjs.utc(date).tz(tz)
  if (!d.isValid()) return fallback

  if (style === 'relative') {
    return addSuffix ? d.fromNow() : d.fromNow(true)
  }

  // Auto-convert to dateTime if showTime is true
  if (showTime && style !== 'dateTime' && style !== 'custom') {
    // Determine time format: use provided timeFormat, or get from settings, default to 24h
    let effectiveTimeFormat: TimeFormat = timeFormat || '24h'

    if (!timeFormat) {
      try {
        const settingValue: string = useAppSettings.getState().getSetting('localization.time_format', '24h')
        effectiveTimeFormat = settingValue === '12h' ? '12h' : '24h'
      } catch {
        effectiveTimeFormat = '24h'
      }
    }

    // Use user's date format setting by default, unless a specific style preset is requested
    const datePattern = getDateFormatPattern()
    const timePattern = effectiveTimeFormat === '12h' ? 'h:mm A' : 'HH:mm'

    return d.format(`${datePattern}, ${timePattern}`)
  }

  if (style === 'dateTime') {
    // Determine time format: use provided timeFormat, or get from settings, default to 24h
    let effectiveTimeFormat: TimeFormat = timeFormat || '24h'

    // Get from app settings if not provided
    if (!timeFormat) {
      try {
        const settingValue: string = useAppSettings.getState().getSetting('localization.time_format', '24h')
        effectiveTimeFormat = settingValue === '12h' ? '12h' : '24h'
      } catch {
        // If store not available, use default 24h
        effectiveTimeFormat = '24h'
      }
    }

    const dateTimePresets = getDateTimePresets(effectiveTimeFormat)
    const formatPattern = dateTimePresets[dateTimeStyle]

    return d.format(formatPattern)
  }

  // For date-only formats, respect the date format setting for 'short' style
  if (style === 'short') {
    const formatPattern = getDateFormatPattern()
    return d.format(formatPattern)
  }

  const fmt = style === 'custom' && formatStr ? formatStr : formatPresets[style as keyof typeof formatPresets]

  return d.format(fmt)
}

// Helpers
export const addDays = (date: Date, days: number) => dayjs(date).add(days, 'day').toDate()
export const subDays = (date: Date, days: number) => dayjs(date).subtract(days, 'day').toDate()
export const addMonths = (date: Date, months: number) => dayjs(date).add(months, 'month').toDate()
export const subMonths = (date: Date, months: number) => dayjs(date).subtract(months, 'month').toDate()
export const addYears = (date: Date, years: number) => dayjs(date).add(years, 'year').toDate()
export const subYears = (date: Date, years: number) => dayjs(date).subtract(years, 'year').toDate()

export const startOfDay = (date: Date) => dayjs(date).startOf('day').toDate()
export const endOfDay = (date: Date) => dayjs(date).endOf('day').toDate()
export const startOfMonth = (date: Date) => dayjs(date).tz(getUserTimezone()).startOf('month').toDate()

export const endOfMonth = (date: Date) => dayjs(date).tz(getUserTimezone()).endOf('month').toDate()
export const startOfYear = (date: Date) => dayjs(date).startOf('year').toDate()
export const endOfYear = (date: Date) => dayjs(date).endOf('year').toDate()
export const startOfWeek = (date: Date) => {
  const tz = getUserTimezone()

  return dayjs(date).tz(tz).startOf('week').toDate()
}

export const endOfWeek = (date: Date) => {
  const tz = getUserTimezone()

  return dayjs(date).tz(tz).endOf('week').toDate()
}

export const differenceInMilliseconds = (dateLeft: Date, dateRight: Date) => dayjs(dateLeft).diff(dayjs(dateRight))

// Parse ISO string to Date
export const parseISO = (dateString: string) => dayjs(dateString).toDate()

// Check if date is valid
export const isValid = (date: Date | string | number) => dayjs(date).isValid()

/**
 * Convert a Date or datetime string to UTC ISO string for API submission
 * For datetime-local inputs, the string is interpreted in user's selected timezone
 * @param date - Date object or datetime-local string (YYYY-MM-DDTHH:mm)
 * @returns ISO 8601 string in UTC (e.g., "2025-12-15T16:30:12.000Z")
 */
export const toUTC = (date: Date | string | null | undefined): string | null => {
  if (!date) return null

  // If it's a string without timezone info (from datetime-local input),
  // parse it in user's selected timezone before converting to UTC
  if (typeof date === 'string' && !date.includes('Z') && !date.includes('+')) {
    const tz = getUserTimezone()
    return dayjs.tz(date, tz).utc().toISOString()
  }

  // Otherwise, parse as-is and convert to UTC
  return dayjs(date).utc().toISOString()
}

/**
 * Parse UTC date from API and convert to user's timezone as Date object
 * Use this when you need a Date object for form inputs (react-hook-form, date pickers)
 * @param utcDate - UTC date string from API
 * @returns Date object in user's timezone
 */
export const fromUTC = (utcDate: string | null | undefined): Date | null => {
  if (!utcDate) return null
  const tz = getUserTimezone()
  return dayjs.utc(utcDate).tz(tz).toDate()
}

/**
 * Format date for HTML input fields (YYYY-MM-DD)
 * @param date - Date object or string
 * @returns String in YYYY-MM-DD format for <input type="date">
 */
export const toInputDate = (date: Date | string | null | undefined): string => {
  if (!date) return ''
  return dayjs(date).format('YYYY-MM-DD')
}

/**
 * Format date as UTC YYYY-MM-DD for API query parameters
 * Converts local timezone date to UTC date format suitable for API filtering
 * @param date - Date object in user's local timezone
 * @returns String in YYYY-MM-DD format (UTC) for API query params
 */
export const toUTCDateString = (date: Date | string | null | undefined): string => {
  if (!date) return ''
  return dayjs(date).utc().format('YYYY-MM-DD')
}

/**
 * Format time for HTML input fields (HH:mm)
 * @param date - Date object or string
 * @returns String in HH:mm format for <input type="time">
 */
export const toInputTime = (date: Date | string | null | undefined): string => {
  if (!date) return ''
  return dayjs(date).format('HH:mm')
}

/**
 * Combine date and time inputs into a Date object
 * @param dateStr - Date string from <input type="date"> (YYYY-MM-DD)
 * @param timeStr - Time string from <input type="time"> (HH:mm)
 * @returns Date object in user's timezone
 */
export const combineDateTime = (dateStr: string, timeStr: string): Date | null => {
  if (!dateStr || !timeStr) return null
  const tz = getUserTimezone()
  return dayjs.tz(`${dateStr} ${timeStr}`, 'YYYY-MM-DD HH:mm', tz).toDate()
}

/**
 * Format UTC date from API for datetime-local input
 * Converts UTC to user's selected timezone and formats for <input type="datetime-local">
 * @param utcDate - UTC date string from API (e.g., "2025-12-10T14:46:00.000Z")
 * @returns String in YYYY-MM-DDTHH:mm format in user's timezone
 */
export const toInputDateTime = (utcDate: string | null | undefined): string => {
  if (!utcDate) return ''
  const tz = getUserTimezone()
  return dayjs.utc(utcDate).tz(tz).format('YYYY-MM-DDTHH:mm')
}

/**
 * Convert a date to midnight in user's selected timezone
 * Use this when receiving dates from UI components (like date pickers) that return
 * dates in browser timezone, to ensure consistency with user's timezone preference
 * @param date - Date object (typically from browser timezone)
 * @returns Date object at midnight in user's selected timezone
 */
export const toUserTimezoneDate = (date: Date | null | undefined): Date | null => {
  if (!date) return null
  const tz = getUserTimezone()
  // Extract YYYY-MM-DD and parse at midnight in user's timezone
  const dateStr = dayjs(date).format('YYYY-MM-DD')
  return dayjs.tz(dateStr, 'YYYY-MM-DD', tz).toDate()
}

/**
 * Parse date-only string from API (e.g., "2025-12-15" or "2025-12-15T16:30:19.000Z")
 * Extracts the date portion and returns it at midnight UTC
 * Use this for date-only fields from API that should be treated as calendar dates
 * without timezone conversions (e.g., birthdate, goal dates, measurement dates)
 * @param dateString - Date string from API (can be date-only or datetime with timezone)
 * @returns Date object at midnight UTC representing the calendar date
 */
export const parseDateOnly = (dateString: string | null | undefined): Date | null => {
  if (!dateString) return null
  // Extract just the date portion (YYYY-MM-DD) from the string
  const dateOnly = dateString.split('T')[0]
  // Parse as UTC midnight to avoid any timezone conversion issues
  // This ensures "2025-12-15" always represents Dec 15 regardless of user/browser timezone
  return dayjs.utc(dateOnly, 'YYYY-MM-DD').toDate()
}

/**
 * Convert time string from user's timezone to UTC time string
 * Used for time-only fields (e.g., class template start times)
 * @param timeStr - Time string in HH:mm or HH:mm:ss format (e.g., "09:00")
 * @returns Time string in HH:mm:ss format in UTC (e.g., "06:00:00" if user is in UTC+3)
 */
export const timeToUTC = (timeStr: string | null | undefined): string | null => {
  if (!timeStr) return null
  const tz = getUserTimezone()
  // Use today's date as reference (arbitrary, only time matters)
  const today = dayjs().format('YYYY-MM-DD')
  // Parse time in user's timezone
  const localDateTime = dayjs.tz(`${today} ${timeStr}`, 'YYYY-MM-DD HH:mm:ss', tz)
  // Convert to UTC and extract time
  return localDateTime.utc().format('HH:mm:ss')
}

/**
 * Convert UTC time string to user's timezone time string
 * Used for displaying time-only fields from API
 * @param utcTimeStr - Time string in HH:mm:ss format from API (in UTC)
 * @returns Time string in HH:mm:ss format in user's timezone
 */
export const timeFromUTC = (utcTimeStr: string | null | undefined): string | null => {
  if (!utcTimeStr) return null
  const tz = getUserTimezone()
  // Use today's date as reference
  const today = dayjs().format('YYYY-MM-DD')
  // Parse as UTC time
  const utcDateTime = dayjs.utc(`${today} ${utcTimeStr}`, 'YYYY-MM-DD HH:mm:ss')
  // Convert to user's timezone and extract time
  return utcDateTime.tz(tz).format('HH:mm:ss')
}
